import { readonly, ref } from "vue"

/** How the open conversation is shown: a floating window, the full thread in the inbox, or a pill. */
export type PatientConversationPanelMode = "floating" | "expanded" | "minimized"

/** Who a new message is addressed to when the composer opens from a chart. */
export interface NewMessageRecipient {
  /** `mbr_…` */
  id: string
  fullName: string
}

// Module-level so the inbox, the chart, the side rail and the Slack deep link all drive one panel.
const openConversationId = ref<string | null>(null)
const mode = ref<PatientConversationPanelMode>("floating")
const composerRecipient = ref<NewMessageRecipient | null>(null)
const composerOpen = ref<boolean>(false)

/** The floating conversation panel and the New message composer, shared across the dashboard. */
export function usePatientConversationPanel() {
  function openConversation(patientConversationId: string): void {
    openConversationId.value = patientConversationId
    if (mode.value === "minimized") mode.value = "floating"
  }

  function closeConversation(): void {
    openConversationId.value = null
    mode.value = "floating"
  }

  const minimize = (): void => {
    mode.value = "minimized"
  }
  const restore = (): void => {
    mode.value = "floating"
  }
  const expand = (): void => {
    mode.value = "expanded"
  }
  const collapse = (): void => {
    mode.value = "floating"
  }

  function openComposer(recipient?: NewMessageRecipient): void {
    composerRecipient.value = recipient ?? null
    composerOpen.value = true
  }

  function closeComposer(): void {
    composerOpen.value = false
    composerRecipient.value = null
  }

  return {
    openConversationId: readonly(openConversationId),
    mode: readonly(mode),
    composerOpen: readonly(composerOpen),
    composerRecipient: readonly(composerRecipient),
    openConversation,
    closeConversation,
    minimize,
    restore,
    expand,
    collapse,
    openComposer,
    closeComposer
  }
}
